import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Pin, Archive, BellOff, Trash2, Eraser, MoreVertical, Plus, X, User as UserIcon, Settings as SettingsIcon, LogOut } from "lucide-react";
import type { Chat, PublicUser } from "@glass/shared";
import { Avatar } from "./Avatar";
import { api } from "../lib/api";
import { useStore, store } from "../lib/store";
import { Modal } from "./Modal";
import { Logo } from "./Logo";

interface SidebarProps {
  onPickChat: (chatId: string) => void;
  activeChatId: string | null;
  onOpenSettings: () => void;
}

function shortTime(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "вчера";
  return d.toLocaleDateString([], { day: "2-digit", month: "2-digit" });
}

function chatPreview(c: Chat): string {
  if (c.type === "saved") return "Избранное — заметки и медиа";
  if (c.type === "channel") return c.username ? `@${c.username} · канал` : "Канал";
  if (c.type === "audio_room") return "Audio Room";
  if (c.member_count > 1) return `${c.member_count} участников`;
  if (c.username) return `@${c.username}`;
  if (c.description) return c.description;
  return "Личный чат";
}

export function Sidebar({ onPickChat, activeChatId, onOpenSettings }: SidebarProps) {
  const chats = useStore((s) => s.chats);
  const me = useStore((s) => s.user);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<{ users: PublicUser[]; chats: Chat[] } | null>(null);
  const [showArchive, setShowArchive] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [topMenu, setTopMenu] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const longPressTimer = useRef<number | null>(null);
  const topMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!query.trim()) { setResults(null); return; }
    const t = setTimeout(async () => {
      try { setResults(await api.search(query.trim())); } catch { setResults(null); }
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!topMenu) return;
    const onClick = (e: MouseEvent) => {
      if (topMenuRef.current && !topMenuRef.current.contains(e.target as Node)) setTopMenu(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [topMenu]);

  const visible = useMemo(() => {
    const filtered = chats.filter((c) => (showArchive ? c.archived : !c.archived));
    return [...filtered].sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      const at = a.last_message_at ?? a.created_at;
      const bt = b.last_message_at ?? b.created_at;
      return (bt || "").localeCompare(at || "");
    });
  }, [chats, showArchive]);

  async function refresh() {
    const r = await api.listChats();
    store.set({ chats: r.chats });
  }
  useEffect(() => { void refresh(); }, []);

  function startLongPress(chatId: string) {
    if (longPressTimer.current) window.clearTimeout(longPressTimer.current);
    longPressTimer.current = window.setTimeout(() => setOpenMenu(chatId), 380);
  }
  function endLongPress() {
    if (longPressTimer.current) { window.clearTimeout(longPressTimer.current); longPressTimer.current = null; }
  }

  async function pin(chat: Chat) { await api.pinChat(chat.id, !chat.pinned); await refresh(); setOpenMenu(null); }
  async function archive(chat: Chat) { await api.archiveChat(chat.id, !chat.archived); await refresh(); setOpenMenu(null); }
  async function mute(chat: Chat) { await api.muteChat(chat.id, !chat.muted); await refresh(); setOpenMenu(null); }
  async function clear(chat: Chat) { if (confirm(`Очистить историю «${chat.title}»?`)) { await api.clearHistory(chat.id); setOpenMenu(null); } }
  async function leave(chat: Chat) { if (confirm(`Удалить чат «${chat.title}»?`)) { await api.leaveChat(chat.id); await refresh(); setOpenMenu(null); } }

  return (
    <aside className="w-full md:w-[22rem] lg:w-[24rem] h-dvh flex flex-col bg-black/40 backdrop-blur-xl border-r border-white/5">
      {/* Header: big chunky wordmark */}
      <div className="px-5 pt-7 pb-3 flex items-end justify-between">
        <Logo size="lg" />
        <button
          className="rounded-full p-1.5 text-ink-300 hover:bg-white/5 transition-colors"
          onClick={onOpenSettings}
          title="Профиль"
          aria-label="Профиль"
        >
          <Avatar name={me?.name || "Me"} url={me?.avatar_url} size={32} />
        </button>
      </div>

      {/* Search pill + 3-dot menu */}
      <div className="px-4 pb-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-400" strokeWidth={2.5} />
          <input
            className="w-full h-12 rounded-full bg-white/5 border border-white/10 pl-11 pr-9 text-[15px] text-ink-100 placeholder:text-ink-400 outline-none focus:border-white/25 focus:bg-white/[0.07] transition-colors"
            placeholder="поиск..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 p-1 rounded-full hover:bg-white/10"
              onClick={() => setQuery("")}
              aria-label="Очистить"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="relative" ref={topMenuRef}>
          <button
            className="h-12 w-12 grid place-items-center rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors text-ink-100"
            onClick={() => setTopMenu((v) => !v)}
            aria-label="Меню"
          >
            <MoreVertical size={20} strokeWidth={2.4} />
          </button>
          {topMenu && (
            <div className="absolute right-0 top-14 z-30 glass-strong p-1 rounded-2xl text-sm min-w-[220px]">
              <button className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/10" onClick={() => { setTopMenu(false); setCreateOpen(true); }}>
                <Plus size={16} /> Новый чат
              </button>
              <button className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/10" onClick={() => { setTopMenu(false); setShowArchive((v) => !v); }}>
                <Archive size={16} /> {showArchive ? "Все чаты" : "Архив"}
              </button>
              <button className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/10" onClick={() => { setTopMenu(false); onOpenSettings(); }}>
                <SettingsIcon size={16} /> Настройки
              </button>
              <div className="my-1 h-px bg-white/10" />
              <button className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/10" onClick={() => { setTopMenu(false); onOpenSettings(); }}>
                <UserIcon size={16} /> Профиль
              </button>
              <button className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-red-500/15 text-red-300" onClick={() => { api.setToken(null); window.location.reload(); }}>
                <LogOut size={16} /> Выйти
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Chat list */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 no-scrollbar space-y-2">
        {results ? (
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-400 mt-1 mb-1 px-2">Пользователи</div>
            {results.users.length === 0 && <div className="px-2 py-2 text-sm text-ink-400">Никого не нашлось</div>}
            {results.users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 px-3 py-3 rounded-3xl bg-white/[0.04] border border-white/5 hover:bg-white/[0.08] cursor-pointer mb-2">
                <Avatar name={u.name} url={u.avatar_url} size={48} online={u.online} />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{u.name}</div>
                  <div className="text-xs text-ink-400 truncate">{u.username ? `@${u.username}` : ""}</div>
                </div>
              </div>
            ))}
            <div className="text-xs uppercase tracking-wider text-ink-400 mt-3 mb-1 px-2">Чаты и каналы</div>
            {results.chats.length === 0 && <div className="px-2 py-2 text-sm text-ink-400">Ничего</div>}
            {results.chats.map((c) => (
              <ChatCard key={c.id} chat={c} active={false} onClick={() => onPickChat(c.id)} />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="p-8 text-center text-ink-400 text-sm">
            Чатов пока нет. Откройте меню → «Новый чат», чтобы создать первый.
          </div>
        ) : (
          visible.map((c) => (
            <div
              key={c.id}
              onPointerDown={() => startLongPress(c.id)}
              onPointerUp={endLongPress}
              onPointerLeave={endLongPress}
              onContextMenu={(e) => { e.preventDefault(); setOpenMenu(c.id); }}
              className="relative"
            >
              <ChatCard chat={c} active={c.id === activeChatId} onClick={() => onPickChat(c.id)} />
              {openMenu === c.id && (
                <div className="absolute right-3 top-3 z-30 glass-strong p-1 rounded-2xl text-sm min-w-[220px]" onClick={(e) => e.stopPropagation()}>
                  <button className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/10" onClick={() => pin(c)}><Pin size={14} /> {c.pinned ? "Открепить" : "Закрепить"}</button>
                  <button className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/10" onClick={() => archive(c)}><Archive size={14} /> {c.archived ? "Из архива" : "В архив"}</button>
                  <button className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/10" onClick={() => mute(c)}><BellOff size={14} /> {c.muted ? "Включить звук" : "Без звука"}</button>
                  <button className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/10" onClick={() => clear(c)}><Eraser size={14} /> Очистить историю</button>
                  <div className="my-1 h-px bg-white/10" />
                  <button className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-red-500/15 text-red-300" onClick={() => leave(c)}><Trash2 size={14} /> Удалить чат</button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <CreateChatModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={async (id) => {
        setCreateOpen(false);
        await refresh();
        onPickChat(id);
      }} />
    </aside>
  );

  function ChatCard({ chat, active, onClick }: { chat: Chat; active: boolean; onClick: () => void }) {
    return (
      <button
        onClick={onClick}
        className={`w-full text-left rounded-3xl px-3 py-3 flex items-center gap-3 transition-colors border ${
          active
            ? "bg-white/[0.10] border-white/20"
            : "bg-white/[0.04] border-white/5 hover:bg-white/[0.07] hover:border-white/10"
        }`}
      >
        <Avatar name={chat.title} url={chat.avatar_url} size={56} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="font-semibold truncate text-[15px]">{chat.title}</div>
            {chat.pinned && <Pin size={12} className="text-accent-300 shrink-0" />}
            {chat.muted && <BellOff size={12} className="text-ink-400 shrink-0" />}
          </div>
          <div className="text-[13px] text-ink-400 truncate mt-0.5">{chatPreview(chat)}</div>
        </div>
        <div className="flex flex-col items-end justify-between self-stretch py-0.5 shrink-0">
          <span className="text-[11px] text-ink-400 tabular-nums">{shortTime(chat.last_message_at)}</span>
          {chat.unread_count > 0 ? (
            <span className="mt-1 inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-full bg-accent-500 text-white text-[11px] font-semibold px-1.5">
              {chat.unread_count > 99 ? "99+" : chat.unread_count}
            </span>
          ) : <span aria-hidden className="h-[22px]" />}
        </div>
      </button>
    );
  }
}

function CreateChatModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const [type, setType] = useState<Chat["type"]>("group");
  const [title, setTitle] = useState("");
  const [username, setUsername] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <Modal open={open} onClose={onClose} title="Новый чат">
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          {(["group", "channel", "community", "audio_room"] as const).map((k) => (
            <button key={k} className={`btn ${type === k ? "btn-primary" : "btn-ghost"}`} onClick={() => setType(k)}>
              {k === "group" && "Группа"}{k === "channel" && "Канал"}{k === "community" && "Сообщество"}{k === "audio_room" && "Audio Room"}
            </button>
          ))}
        </div>
        <input className="glass-input w-full" placeholder="Название" value={title} onChange={(e) => setTitle(e.target.value)} />
        <input className="glass-input w-full" placeholder="Публичная ссылка (опционально)" value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} />
        <label className="flex items-center gap-2 text-sm text-ink-300"><input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} /> Публичный (доступен в поиске)</label>
        {err && <div className="text-sm text-red-300">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" disabled={busy || !title.trim()} onClick={async () => {
            try {
              setBusy(true); setErr(null);
              const r = await api.createChat({ type, title: title.trim(), username: username.trim() || undefined, is_public: isPublic });
              onCreated(r.chat.id);
            } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
            finally { setBusy(false); }
          }}>Создать</button>
        </div>
      </div>
    </Modal>
  );
}
